// socket/messagerieSocket.js
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 
                   (process.env.NODE_ENV === "production" 
                     ? "https://api.pesacore.fr" 
                     : "https://localhost:5000");

class MessagerieSocket {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.listeners = new Map();
  }
 
  connect() {
    if (this.socket?.connected) {
      console.log('⚠️ Messagerie socket déjà connecté');
      return;
    }

    this.socket = io(`${SOCKET_URL}/messagerie`, {
      withCredentials: true,
      transports: process.env.NODE_ENV === 'development' ? ['polling'] : ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    // ═══════════════════════════════════════════════════════════════════════
    // ÉVÉNEMENTS SYSTÈME
    // ═══════════════════════════════════════════════════════════════════════

    this.socket.on('connect', () => {
      console.log('[MESSAGERIE] Connecté au serveur');
      this.connected = true;

      // === CORRECTION : On rejoint automatiquement la room personnelle ===
      if (this.userId) {
        console.log(`[MESSAGERIE] Envoi joinUserRoom pour userId: ${this.userId}`);
        this.socket.emit('joinUserRoom', this.userId);
      }

      this.emit('connected', { socketId: this.socket.id });
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ [MESSAGERIE] Erreur connexion:', error.message);
      this.connected = false;
      this.emit('error', { type: 'connection', message: error.message });
    });

    this.socket.on('disconnect', (reason) => {
      console.log(`⚠️ [MESSAGERIE] Déconnecté: ${reason}`);
      this.connected = false;
      this.emit('disconnected', { reason });
    });

    // Confirmation que la room a bien été rejointe
    this.socket.on('messagerie:room-joined', (data) => {
      console.log('[MESSAGERIE] Room personnelle rejointe avec succès:', data.room);
    });

    // ═══════════════════════════════════════════════════════════════════════
    // ÉVÉNEMENTS MESSAGERIE (listeners existants)
    // ═══════════════════════════════════════════════════════════════════════

    this.socket.on('messagerie:inbox', (data) => {
      this.emit('inbox', data);
    });

    this.socket.on('messagerie:sent', (data) => {
      this.emit('sent', data);
    });

    this.socket.on('messagerie:new-message', (data) => {
      console.log('📨 [MESSAGERIE] Nouveau message reçu:', data);
      this.emit('new-message', data);
      
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(data.notification.title, {
          body: data.notification.body,
          icon: '/logo192.png',
          tag: data.message?._id
        });
      }
    });

    this.socket.on('messagerie:message-sent', (data) => {
      this.emit('message-sent', data);
    });

    this.socket.on('messagerie:message-read', (data) => {
      this.emit('message-read', data);
    });

    this.socket.on('messagerie:read-receipt', (data) => {
      this.emit('read-receipt', data);
    });

    this.socket.on('messagerie:message-archived', (data) => {
      this.emit('message-archived', data);
    });

    this.socket.on('messagerie:reply-sent', (data) => {
      this.emit('reply-sent', data);
    });

    this.socket.on('messagerie:unread-count', (data) => {
      this.emit('unread-count', data);
    });

    this.socket.on('messagerie:error', (data) => {
      console.error('❌ [MESSAGERIE] Erreur:', data);
      this.emit('error', data);
    });

    this.socket.on('messagerie:pong', (data) => {
      this.emit('pong', data);
    });
  }
  
  // Nouvelle méthode pour indiquer quel utilisateur on est
  setUserId(userId) {
    this.userId = userId;
    // Si déjà connecté, on rejoint immédiatement la room
    if (this.socket?.connected && userId) {
      console.log(`[MESSAGERIE] Re-joinUserRoom après changement de profil: ${userId}`);
      this.socket.emit('joinUserRoom', userId);
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      console.log('🔌 [MESSAGERIE] Déconnecté manuellement');
    }
  }

  // === Tes méthodes existantes (inchangées) ===
  getInbox(options = {}) {
    if (!this.connected) return;
    this.socket.emit('messagerie:get-inbox', options);
  }

  getSent(options = {}) {
    if (!this.connected) return;
    this.socket.emit('messagerie:get-sent', options);
  }

  sendMessage(messageData) {
    if (!this.connected) return;
    this.socket.emit('messagerie:send', messageData);
  }
  
  markAsRead(messageId) {
    if (!this.connected) return;
    this.socket.emit('messagerie:mark-read', { messageId });
  }
  
  archiveMessage(messageId) {
    if (!this.connected) return;
    this.socket.emit('messagerie:archive', { messageId });
  }
  
  replyToMessage(messageId, body) {
    if (!this.connected) return;
    this.socket.emit('messagerie:reply', { messageId, body });
  }
  
  getUnreadCount() {
    if (!this.connected) return;
    this.socket.emit('messagerie:get-unread-count');
  }

  ping() {
    if (!this.connected) return;
    this.socket.emit('messagerie:ping');
  }

  toggleStar(messageId) {
    if (!this.connected) return;
    this.socket.emit('messagerie:toggle-star', { messageId });
  }

  addLabel(messageId, labelId) {
    if (!this.connected) return;
    this.socket.emit('messagerie:add-label', { messageId, labelId });
  }

  sendSMS(to, body) {
    if (!this.connected) return;
    this.socket.emit('messagerie:send-sms', { to, body });
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback);
  }
  
  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) callbacks.splice(index, 1);
    if (callbacks.length === 0) this.listeners.delete(event);
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;
    this.listeners.get(event).forEach(callback => {
      try { callback(data); } 
      catch (err) { console.error(`[MESSAGERIE] Erreur callback ${event}:`, err); }
    });
  }

  cleanup() {
    this.listeners.clear();
    this.disconnect();
  }
}

const messagerieSocket = new MessagerieSocket();
export default messagerieSocket;